// Repo 详情页(Fluent UI v9 版,从 antd 6 迁移)
//
// 布局:
//   ┌─────────────────────────────────────────────┐
//   │ owner/name  [public]                        │  ← 头部信息行
//   │ description (可选)                          │
//   │ [HTTP|HTTPS] [Clone: URL] [▼ main]          │  ← 操作栏(响应式 flex)
//   ├─────────────────────────────────────────────┤
//   │ [文件] [提交] [分支] [设置] [协作者]         │  ← TabList
//   ├─────────────────────────────────────────────┤
//   │ 文件树(左) | 文件内容(右)                   │  ← 或 commit 列表 / 设置表单
//   └─────────────────────────────────────────────┘
import { useState, useMemo, useEffect, lazy, Suspense, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  makeStyles,
  tokens,
  mergeClasses,
  Button,
  Input,
  Textarea,
  Text,
  Badge,
  Spinner,
  TabList,
  Tab,
  Card,
  CardHeader,
  Combobox,
  Option,
  Divider,
  Tooltip,
  Avatar,
  Label,
  Switch,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  MessageBar,
  MessageBarBody,
  useId,
} from '@fluentui/react-components';
import {
  CopyRegular,
  ArrowClockwiseRegular,
  FolderRegular,
  DocumentRegular,
  HistoryRegular,
  SettingsRegular,
  BranchForkRegular,
  AddRegular,
  EditRegular,
  DeleteRegular,
  PeopleTeamRegular,
  ChevronRightRegular,
  ChevronDownRegular,
} from '@fluentui/react-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { reposApi } from '@/api/repos';
import { gitApi } from '@/api/git';
import type { CommitSummary, TreeEntry, BlobMeta, ServerInfo } from '@/api/git';
import type { Collaborator } from '@/api/types';
import { explainError } from '@/api/client';
import { useAuthStore } from '@/store/auth';
import { toast } from '@/lib/toast';
import { HelpPopover } from '@/components/HelpPopover';

// Monaco 体积大,仅在开启"原始文本"时才按需加载
const MonacoRawEditor = lazy(() =>
  import('@/components/MonacoRawEditor').then((m) => ({
    default: m.MonacoRawEditor,
  })),
);

// 提交详情对话框同样重度依赖 Monaco,仅在点开时加载,避免打进主 bundle
const CommitDetailDialog = lazy(() =>
  import('@/components/CommitDetailDialog').then((m) => ({
    default: m.CommitDetailDialog,
  })),
);

// ============================================================
// 样式
// ============================================================
const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%', // 占满 contentBody 可视区;内部 tabPanel 滚动,面板再各自滚动
    overflow: 'hidden',
    gap: '16px',
  },
  headerSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  titleText: {
    margin: 0,
    lineHeight: '1.3',
  },
  descText: {
    margin: 0,
    maxWidth: '800px',
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
  },
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  schemeToggle: {
    display: 'inline-flex',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  cloneInput: {
    flex: 1,
    minWidth: '200px',
    maxWidth: '520px',
  },
  branchSelect: {
    width: '180px',
    minWidth: '140px',
  },
  tabsRow: {
    marginTop: '4px',
  },
  tabPanel: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    marginTop: '8px',
  },
  panelBody: {
    padding: '16px',
  },
  panelBodyCompact: {
    padding: '8px',
  },
  cardHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    minWidth: 0,
  },
  cardHeaderText: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    maxWidth: '300px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // 统一滚动条规范(ui_scroller §5):细滚动条,默认透明隐藏,悬浮显示
  uvScroll: {
    '::-webkit-scrollbar': { width: '8px', height: '8px' },
    '::-webkit-scrollbar-track': { background: 'transparent' },
    '::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: '4px' },
    ':hover::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.25)' },
    ':hover::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.04)' },
    scrollbarWidth: 'thin',
    scrollbarColor: 'transparent transparent',
    ':hover': { scrollbarColor: 'rgba(0,0,0,0.25) transparent' },
  },
  // FilesTab
  filesLayout: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    gap: '16px',
    overflow: 'hidden',
    alignItems: 'stretch',
  },
  treeCard: {
    width: '280px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  treeBody: {
    flex: 1,
    minHeight: 0,
    padding: '8px',
    overflowY: 'auto',
  },
  blobCard: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  blobBody: {
    flex: 1,
    minHeight: 0,
    padding: '16px',
    overflow: 'auto',
  },
  // 原始文本(Monaco)模式:去掉内边距并让编辑器接管自身滚动,避免双重滚动条
  blobBodyRaw: {
    padding: 0,
    overflow: 'hidden',
    display: 'flex',
  },
  // commit list
  commitItem: {
    padding: '12px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    marginTop: '2px',
    ':hover': { backgroundColor: tokens.colorNeutralBackground2 },
  },
  commitTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    marginBottom: '4px',
  },
  commitSubject: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  commitOid: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
    padding: '1px 6px',
    borderRadius: '4px',
  },
  commitBody: {
    margin: '4px 0',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'pre-wrap',
  },
  commitMeta: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  // branches
  branchToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  branchList: {
    display: 'flex',
    flexDirection: 'column',
  },
  branchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    height: '44px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: '0 4px',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  branchName: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  branchActions: {
    marginLeft: 'auto',
    display: 'flex',
    gap: '4px',
  },
  // collaborators
  collabSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  collabAddRow: {
    display: 'flex',
    gap: '8px',
    marginTop: '8px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  collabRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  collabMain: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    flex: 1,
  },
  collabName: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  collabDesc: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  // settings
  settingsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  settingsField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  fieldLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  fieldHint: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  charCount: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'right',
  },
  dangerBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  dangerTitle: {
    color: tokens.colorStatusDangerForeground1,
    fontWeight: 600,
  },
  dangerButton: {
    backgroundColor: tokens.colorStatusDangerBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
    ':hover': {
      backgroundColor: tokens.colorStatusDangerBackground3Hover,
    },
    ':active': {
      backgroundColor: tokens.colorStatusDangerBackground3Pressed,
    },
  },
  pre: {
    margin: 0,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '13px',
    whiteSpace: 'pre',
    lineHeight: '1.5',
    backgroundColor: tokens.colorNeutralBackground2,
    padding: '12px',
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'auto',
  },
  emptyCenter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '32px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
  loadingCenter: {
    display: 'flex',
    justifyContent: 'center',
    padding: '48px',
  },
});

// 文件树专用样式
const useFileTreeStyles = makeStyles({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    height: '28px',
    paddingRight: '8px',
    cursor: 'pointer',
    borderRadius: '4px',
    userSelect: 'none',
    color: tokens.colorNeutralForeground1,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  rowSelected: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    color: tokens.colorBrandForeground1,
    fontWeight: 600,
  },
  chevron: {
    display: 'inline-flex',
    width: '16px',
    justifyContent: 'center',
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  icon: {
    display: 'inline-flex',
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  label: {
    fontSize: '13px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  commitSubj: {
    marginLeft: 'auto',
    maxWidth: '180px',
    minWidth: 0,
    flexShrink: 1,
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    userSelect: 'none',
  },
  spinnerWrap: {
    marginLeft: 'auto',
  },
});

// ============================================================
// 辅助函数
// ============================================================
const README_RE = /^readme(\.|$)/i;

function isReadmeFile(name: string): boolean {
  return README_RE.test(name);
}

function authHeader(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { authorization: `Bearer ${token}` } : {};
}

interface FileNode {
  key: string;
  title: string;
  isLeaf: boolean;
  icon: ReactNode;
  /** 该路径最近一次提交的标题,用于节点右侧展示 */
  lastCommit?: string;
}

function entryToFileNode(e: TreeEntry): FileNode {
  const isDir = e.kind === 'tree';
  return {
    key: e.path,
    title: e.name,
    isLeaf: !isDir,
    icon: isDir ? <FolderRegular /> : <DocumentRegular />,
    lastCommit: e.last_commit ?? undefined,
  };
}

/** 读取子目录条目(原 antd Tree.loadData 的等价 fetch) */
async function readTreePath(
  owner: string,
  name: string,
  refName: string,
  path: string,
): Promise<TreeEntry[]> {
  const resp = await fetch(
    `/api/v1/gitdata/${encodeURIComponent(owner)}/${encodeURIComponent(
      name,
    )}/tree/${encodeURIComponent(refName)}?path=${encodeURIComponent(path)}`,
    { headers: authHeader() },
  );
  if (!resp.ok) {
    throw new Error(`加载子目录失败: ${resp.status}`);
  }
  const data: { entries: TreeEntry[] } = await resp.json();
  return data.entries;
}

// ============================================================
// ConfirmDialog —— 替代 antd Popconfirm(用 Dialog 实现)
// ============================================================
function ConfirmDialog(props: {
  open: boolean;
  title: string;
  content?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const {
    open,
    title,
    content,
    confirmText = '确认',
    cancelText = '取消',
    danger,
    loading,
    onConfirm,
    onClose,
  } = props;
  const styles = useStyles();
  return (
    <Dialog
      open={open}
      onOpenChange={(_, d) => {
        if (!d.open) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          {content && <DialogContent>{content}</DialogContent>}
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              {cancelText}
            </Button>
            <Button
              appearance="primary"
              className={danger ? styles.dangerButton : undefined}
              onClick={onConfirm}
              disabled={loading}
            >
              {loading ? '处理中…' : confirmText}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ============================================================
// FileTree —— 自定义递归文件树(替代 antd Tree,支持懒加载展开/折叠)
// ============================================================
function FileTree(props: {
  nodes: FileNode[];
  selectedKey: string;
  onSelect: (key: string) => void;
  loadChildren: (key: string) => Promise<FileNode[]>;
  onLoadError: (msg: string) => void;
}) {
  const { nodes, selectedKey, onSelect, loadChildren, onLoadError } = props;
  const styles = useFileTreeStyles();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());
  const [childrenMap, setChildrenMap] = useState<Record<string, FileNode[]>>(
    {},
  );

  const toggle = async (node: FileNode) => {
    if (node.isLeaf) {
      onSelect(node.key);
      return;
    }
    const isExpanded = expanded.has(node.key);
    if (isExpanded) {
      setExpanded((prev) => {
        const next = new Set(prev);
        next.delete(node.key);
        return next;
      });
      return;
    }
    // 尚未加载子节点则先拉取
    if (!childrenMap[node.key]) {
      setLoadingKeys((prev) => new Set(prev).add(node.key));
      try {
        const kids = await loadChildren(node.key);
        setChildrenMap((prev) => ({ ...prev, [node.key]: kids }));
      } catch (e) {
        onLoadError(explainError(e));
        setLoadingKeys((prev) => {
          const next = new Set(prev);
          next.delete(node.key);
          return next;
        });
        return;
      }
      setLoadingKeys((prev) => {
        const next = new Set(prev);
        next.delete(node.key);
        return next;
      });
    }
    setExpanded((prev) => new Set(prev).add(node.key));
  };

  // 扁平化(带缩进深度)
  const rows: { node: FileNode; depth: number }[] = [];
  const walk = (list: FileNode[], depth: number) => {
    for (const n of list) {
      rows.push({ node: n, depth });
      if (!n.isLeaf && expanded.has(n.key)) {
        const kids = childrenMap[n.key];
        if (kids) walk(kids, depth + 1);
      }
    }
  };
  walk(nodes, 0);

  return (
    <div role="tree">
      {rows.map(({ node, depth }) => {
        const isExpanded = expanded.has(node.key);
        const selected = node.key === selectedKey;
        const isLoading = loadingKeys.has(node.key);
        return (
          <div
            key={node.key}
            className={mergeClasses(
              styles.row,
              selected && styles.rowSelected,
            )}
            style={{ paddingLeft: 8 + depth * 16 }}
            onClick={() => toggle(node)}
          >
            <span className={styles.chevron}>
              {!node.isLeaf ? (
                isExpanded ? (
                  <ChevronDownRegular />
                ) : (
                  <ChevronRightRegular />
                )
              ) : null}
            </span>
            <span className={styles.icon}>{node.icon}</span>
            <span className={styles.label}>{node.title}</span>
            {node.lastCommit && (
              <Tooltip
                content={node.lastCommit}
                relationship="label"
                withArrow
              >
                <span className={styles.commitSubj}>{node.lastCommit}</span>
              </Tooltip>
            )}
            {isLoading && (
              <span className={styles.spinnerWrap}>
                <Spinner size="tiny" />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// RepoDetailPage 主组件
// ============================================================
export function RepoDetailPage({
  owner: ownerProp,
  name: nameProp,
}: {
  /** 可选:嵌入式(对话框)用法时直接传入;缺省时回退路由参数 */
  owner?: string;
  name?: string;
}) {
  const { owner: routeOwner, name: routeName } = useParams<{
    owner: string;
    name: string;
  }>();
  const owner = ownerProp ?? routeOwner;
  const name = nameProp ?? routeName;
  const { user } = useAuthStore();
  const qc = useQueryClient();
  const styles = useStyles();

  const [selectedRef, setSelectedRef] = useState<string>('main');
  const [selectedPath, setSelectedPath] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('files');

  // ═══ SSE 实时刷新:仓库被 push 后自动重载当前仓库数据 ═══
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => {
    if (!owner || !name) return;
    const token = useAuthStore.getState().accessToken;
    if (!token) return;

    const source = new EventSource(
      `/api/v1/repos/events?access_token=${encodeURIComponent(token)}`,
    );
    const onEvent = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data) as {
          owner: string;
          name: string;
        };
        if (data.owner !== owner || data.name !== name) return;
        setRefreshing(true);
        qc.invalidateQueries({ queryKey: ['repo', owner, name] });
        qc.invalidateQueries({ queryKey: ['repo-refs', owner, name] });
        qc.invalidateQueries({ queryKey: ['commits', owner, name] });
        qc.invalidateQueries({ queryKey: ['tree', owner, name] });
        qc.invalidateQueries({ queryKey: ['blob', owner, name] });
        // 短暂展示进度指示后隐藏
        window.setTimeout(() => setRefreshing(false), 800);
      } catch {
        /* 忽略无法解析的事件 */
      }
    };
    source.addEventListener('repo_updated', onEvent);
    return () => {
      source.removeEventListener('repo_updated', onEvent);
      source.close();
    };
  }, [owner, name, qc]);

  // repo metadata
  const {
    data: repo,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['repo', owner, name],
    queryFn: () => reposApi.getRepo(owner!, name!),
    enabled: !!owner && !!name,
  });

  // refs(branches + tags)
  const { data: refs = [] } = useQuery({
    queryKey: ['repo-refs', owner, name],
    queryFn: () => gitApi.listRefs(owner!, name!),
    enabled: !!owner && !!name,
    staleTime: 60_000,
  });

  const branchRefs = useMemo(
    () => refs.filter((r) => r.name.startsWith('refs/heads/')),
    [refs],
  );

  // 第一次拿到 branches 后,默认选 repo.default_branch
  useEffect(() => {
    if (repo && branchRefs.length > 0) {
      const exists = branchRefs.some(
        (r) => r.name === `refs/heads/${repo.default_branch}`,
      );
      if (exists) {
        setSelectedRef(repo.default_branch);
      } else if (branchRefs[0]) {
        setSelectedRef(branchRefs[0].name.replace('refs/heads/', ''));
      }
    }
  }, [repo, branchRefs]);

  // 服务端协议/端口信息(供 clone URL 切换器使用)
  const [serverInfo, setServerInfo] = useState<ServerInfo | null>(null);
  const [scheme, setScheme] = useState<'http' | 'https'>(
    () => (window.location.protocol === 'https:' ? 'https' : 'http'),
  );
  useEffect(() => {
    gitApi
      .serverInfo()
      .then(setServerInfo)
      .catch(() => setServerInfo(null));
  }, []);

  if (isLoading) {
    return (
      <div className={styles.loadingCenter}>
        <Spinner />
      </div>
    );
  }
  if (error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{explainError(error)}</MessageBarBody>
      </MessageBar>
    );
  }
  if (!repo) return null;

  // Owner 判断:当前登录用户是否是仓库拥有者(回退用)
  const isOwner = user ? repo.owner === user.username : false;
  const isAdminUser = user?.is_admin ?? false;
  // 细粒度权限:优先用后端返回的 permissions,回退到 isOwner / 超管判断
  const perms = repo.permissions;
  const canPush = perms?.can_push ?? (isOwner || isAdminUser);
  const canManage = perms?.can_manage ?? (isOwner || isAdminUser);
  const canAdmin = perms?.can_admin ?? (isOwner || isAdminUser);

  // clone URL 协议切换(HTTP / HTTPS)
  const httpsEnabled = serverInfo?.https.enabled ?? false;
  const effectiveScheme: 'http' | 'https' = httpsEnabled ? scheme : 'http';
  const clonePort =
    effectiveScheme === 'https'
      ? serverInfo?.https.port ?? 443
      : serverInfo?.http.port ?? 80;
  const currentCloneUrl = gitApi.cloneUrl(
    effectiveScheme,
    owner!,
    name!,
    clonePort,
    serverInfo?.host,
  );

  const copyClone = async () => {
    try {
      await navigator.clipboard.writeText(currentCloneUrl);
      toast.success('Clone URL 已复制');
    } catch {
      toast.error('复制失败');
    }
  };

  return (
    <div className={styles.root}>
      {/* ═══ 仓库头部 ═══ */}
      <div className={styles.headerSection}>
        <div className={styles.titleRow}>
          {refreshing && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Spinner size="tiny" />
              <Text size={200}>
                检测到新推送，正在重新加载…
              </Text>
            </span>
          )}
        </div>

        {repo.description && (
          <Text as="p" block className={styles.descText}>
            {repo.description}
          </Text>
        )}

        {/* 操作栏:Clone URL + Branch 选择器 */}
        <div className={styles.actionBar}>
          <div className={styles.schemeToggle}>
            <Button
              size="small"
              appearance={effectiveScheme === 'http' ? 'primary' : 'subtle'}
              onClick={() => setScheme('http')}
            >
              HTTP
            </Button>
            <Button
              size="small"
              appearance={effectiveScheme === 'https' ? 'primary' : 'subtle'}
              disabled={!httpsEnabled}
              onClick={() => setScheme('https')}
            >
              HTTPS
            </Button>
          </div>
          <Input
            value={currentCloneUrl}
            readOnly
            className={styles.cloneInput}
            contentBefore={<Text size={200}>Clone</Text>}
            contentAfter={
              <Tooltip content="复制 Clone URL" relationship="label">
                <Button
                  size="small"
                  appearance="transparent"
                  icon={<CopyRegular />}
                  onClick={copyClone}
                />
              </Tooltip>
            }
          />
          <Combobox
            className={styles.branchSelect}
            placeholder="选择 branch"
            value={selectedRef}
            selectedOptions={[selectedRef]}
            onOptionSelect={(_, d) => {
              const v = d.selectedOptions[0];
              if (v) setSelectedRef(v);
            }}
          >
            {branchRefs.map((r) => {
              const label = r.name.replace('refs/heads/', '');
              return (
                <Option key={label} value={label}>
                  {label}
                </Option>
              );
            })}
          </Combobox>
        </div>
      </div>

      {/* ═══ Tabs ═══ */}
      <TabList
        selectedValue={activeTab}
        onTabSelect={(_, d) => setActiveTab(d.value as string)}
        className={styles.tabsRow}
      >
        <Tab value="files">
          <DocumentRegular /> 文件
        </Tab>
        <Tab value="commits">
          <HistoryRegular /> 提交
        </Tab>
        <Tab value="branches">
          <BranchForkRegular /> 分支
        </Tab>
        {canPush && (
          <Tab value="settings">
            <SettingsRegular /> 设置
          </Tab>
        )}
        {canManage && (
          <Tab value="collaborators">
            <PeopleTeamRegular /> 协作者
          </Tab>
        )}
      </TabList>

      <div className={styles.tabPanel}>
        {activeTab === 'files' && (
          <FilesTab
            owner={owner!}
            name={name!}
            refName={selectedRef}
            selectedPath={selectedPath}
            onSelectPath={setSelectedPath}
          />
        )}
        {activeTab === 'commits' && (
          <CommitsTab owner={owner!} name={name!} refName={selectedRef} />
        )}
        {activeTab === 'branches' && (
          <BranchesTab
            owner={owner!}
            name={name!}
            defaultBranch={repo.default_branch}
            canPush={canPush}
          />
        )}
        {activeTab === 'settings' && canPush && (
          <SettingsTab
            owner={owner!}
            name={name!}
            canAdmin={canAdmin}
            onSaved={() =>
              qc.invalidateQueries({ queryKey: ['repo', owner, name] })
            }
            onDeleted={() => {
              qc.invalidateQueries({ queryKey: ['my-repos'] });
              qc.invalidateQueries({ queryKey: ['explore-repos'] });
            }}
          />
        )}
        {activeTab === 'collaborators' && canManage && (
          <CollaboratorsTab owner={owner!} name={name!} />
        )}
      </div>
    </div>
  );
}

// ============================================================
// FilesTab — file tree(左) + 选中文件渲染(右)
// ============================================================
function FilesTab(props: {
  owner: string;
  name: string;
  refName: string;
  selectedPath: string;
  onSelectPath: (path: string) => void;
}) {
  const { owner, name, refName, selectedPath, onSelectPath } = props;
  const styles = useStyles();
  const treePath = '';
  const [rawMode, setRawMode] = useState(false);

  // 根 tree
  const {
    data: rootTree,
    isLoading: rootLoading,
    error: rootError,
  } = useQuery({
    queryKey: ['tree', owner, name, refName, treePath],
    queryFn: () => gitApi.readTree(owner, name, refName),
    enabled: !!refName,
  });

  // 默认选中 README
  useEffect(() => {
    if (!rootTree?.entries || selectedPath) return;
    const readme = rootTree.entries.find((e) => isReadmeFile(e.name));
    if (readme) {
      onSelectPath(readme.path);
    }
  }, [rootTree?.entries, selectedPath, onSelectPath]);

  // 选中的文件(blob)
  const {
    data: blob,
    isLoading: blobLoading,
    error: blobError,
  } = useQuery({
    queryKey: ['blob', owner, name, refName, selectedPath],
    queryFn: () => gitApi.readBlobMeta(owner, name, refName, selectedPath),
    enabled: !!selectedPath,
    retry: false,
  });

  if (rootError) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{explainError(rootError)}</MessageBarBody>
      </MessageBar>
    );
  }

  const treeData: FileNode[] | undefined = rootTree?.entries.map((e) =>
    entryToFileNode(e),
  );

  return (
    <div className={styles.filesLayout}>
      {/* 左:文件树 */}
      <Card size="small" className={styles.treeCard}>
        <CardHeader
          header={
            <span className={styles.cardHeaderRow}>
              <FolderRegular />
              <span>{treePath || '根目录'}</span>
            </span>
          }
        />
        <div className={mergeClasses(styles.treeBody, styles.uvScroll)}>
          {rootLoading ? (
            <Spinner />
          ) : !treeData || treeData.length === 0 ? (
            <div className={styles.emptyCenter}>空仓库,还没有任何文件</div>
          ) : (
            <FileTree
              nodes={treeData}
              selectedKey={selectedPath}
              onSelect={onSelectPath}
              loadChildren={(key) =>
                readTreePath(owner, name, refName, key).then((entries) =>
                  entries.map(entryToFileNode),
                )
              }
              onLoadError={(msg) => toast.error(msg)}
            />
          )}
        </div>
      </Card>

      {/* 右:文件内容 */}
      <Card size="small" className={styles.blobCard}>
        <CardHeader
          header={
            <span className={styles.cardHeaderRow}>
              <DocumentRegular />
              <span className={styles.cardHeaderText}>
                {selectedPath || '请选择文件'}
              </span>
              {blob && (
                <Badge appearance="tint" color="informative">
                  {blob.size} bytes
                </Badge>
              )}
              {blob && (
                <Badge appearance="tint" color="brand">
                  {blob.mime}
                </Badge>
              )}
              {blob && !blob.is_binary && (
                <Switch
                  label="原始文本"
                  checked={rawMode}
                  onChange={(_, d) => setRawMode(d.checked)}
                />
              )}
            </span>
          }
        />
        <div
          className={mergeClasses(
            styles.blobBody,
            styles.uvScroll,
            rawMode && blob && !blob.is_binary && styles.blobBodyRaw,
          )}
        >
          {rawMode && blob && !blob.is_binary ? (
            <Suspense
              fallback={
                <div className={styles.loadingCenter}>
                  <Spinner label="加载编辑器…" />
                </div>
              }
            >
              <MonacoRawEditor path={selectedPath} value={blob.text ?? ''} />
            </Suspense>
          ) : !selectedPath ? (
            <div className={styles.emptyCenter}>
              <DocumentRegular />
              <span>从左侧选一个文件查看内容</span>
            </div>
          ) : blobLoading ? (
            <Spinner />
          ) : blobError ? (
            <MessageBar intent="error">
              <MessageBarBody>{explainError(blobError)}</MessageBarBody>
            </MessageBar>
          ) : blob ? (
            <BlobRenderer blob={blob} />
          ) : null}
        </div>
      </Card>
    </div>
  );
}

function BlobRenderer({ blob }: { blob: BlobMeta }) {
  const styles = useStyles();
  if (blob.is_binary) {
    if (blob.mime.startsWith('image/')) {
      return (
        <div className={styles.emptyCenter}>
          <Text>二进制图片 — {blob.mime}, {blob.size} bytes</Text>
          <Text size={200}>浏览器内嵌渲染 Phase 5 优化</Text>
        </div>
      );
    }
    return (
      <div className={styles.emptyCenter}>
        <Text>
          二进制文件 ({blob.mime}) — {blob.size} bytes
        </Text>
        <Text size={200}>浏览器不支持直接预览,请用 git CLI 拉取</Text>
      </div>
    );
  }
  if (blob.mime === 'text/markdown') {
    return (
      <div style={{ padding: '0 8px' }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{blob.text ?? ''}</ReactMarkdown>
      </div>
    );
  }
  return <pre className={styles.pre}>{blob.text ?? ''}</pre>;
}

// ============================================================
// CommitsTab — commit 历史 list
// ============================================================
function CommitsTab(props: { owner: string; name: string; refName: string }) {
  const { owner, name, refName } = props;
  const styles = useStyles();
  const [selectedCommit, setSelectedCommit] = useState<CommitSummary | null>(null);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['commits', owner, name, refName],
    queryFn: () => gitApi.listCommits(owner, name, refName, 50),
    enabled: !!refName,
  });

  if (isLoading) {
    return (
      <div className={styles.loadingCenter}>
        <Spinner />
      </div>
    );
  }
  if (error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{explainError(error)}</MessageBarBody>
      </MessageBar>
    );
  }

  const commits: CommitSummary[] = data?.commits ?? [];

  return (
    <Card size="small">
      <CardHeader
        header={
          <span className={styles.cardHeaderRow}>
            <HistoryRegular />
            <span>最近 50 个 commit</span>
            <Text size={200}>on {refName}</Text>
          </span>
        }
        action={
          <Button
            appearance="subtle"
            size="small"
            icon={<ArrowClockwiseRegular />}
            onClick={() => refetch()}
            disabled={isFetching}
          >
            刷新
          </Button>
        }
      />
      <div className={styles.panelBody}>
        {commits.length === 0 ? (
          <div className={styles.emptyCenter}>
            <HistoryRegular />
            <span>该仓库还没有任何提交记录</span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {commits.map((c, i) => (
              <div key={c.oid}>
                <div
                  className={styles.commitItem}
                  onClick={() => setSelectedCommit(c)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setSelectedCommit(c);
                  }}
                >
                  <div className={styles.commitTop}>
                    <Text className={styles.commitSubject}>{c.subject}</Text>
                    <span className={styles.commitOid}>{c.short_oid}</span>
                  </div>
                  {c.message !== c.subject && (
                    <Text block className={styles.commitBody}>
                      {c.message.split('\n').slice(1).join('\n').trim()}
                    </Text>
                  )}
                  <Text className={styles.commitMeta}>
                    {c.author_name} &lt;{c.author_email}&gt; ·{' '}
                    {new Date(c.authored_at).toLocaleString('zh-CN')}
                  </Text>
                </div>
                {i < commits.length - 1 && <Divider />}
              </div>
            ))}
          </div>
        )}
      </div>
      {selectedCommit && (
        <Suspense fallback={null}>
          <CommitDetailDialog
            owner={owner}
            name={name}
            commit={selectedCommit}
            onOpenChange={(open) => !open && setSelectedCommit(null)}
          />
        </Suspense>
      )}
    </Card>
  );
}

// ============================================================
// BranchesTab — 分支管理(列表 + 创建 / 删除 / 重命名)
// ============================================================
function BranchesTab(props: {
  owner: string;
  name: string;
  defaultBranch: string;
  canPush: boolean;
}) {
  const { owner, name, defaultBranch, canPush } = props;
  const styles = useStyles();
  const qc = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [fromBranch, setFromBranch] = useState(defaultBranch);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameTo, setRenameTo] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fromId = useId();
  const newNameId = useId();
  const renameId = useId();

  const { data: refs = [], isLoading } = useQuery({
    queryKey: ['repo-refs', owner, name],
    queryFn: () => gitApi.listRefs(owner, name),
  });
  const branches = useMemo(
    () =>
      refs
        .filter((r) => r.name.startsWith('refs/heads/'))
        .map((r) => r.name.replace('refs/heads/', '')),
    [refs],
  );

  const refresh = () =>
    qc.invalidateQueries({ queryKey: ['repo-refs', owner, name] });

  const doCreate = async () => {
    const name0 = newName.trim();
    if (!name0) {
      toast.warning('请输入分支名');
      return;
    }
    try {
      await gitApi.createBranch(owner, name, { name: name0, from: fromBranch });
      toast.success(`分支 ${name0} 已创建`);
      setCreateOpen(false);
      setNewName('');
      refresh();
    } catch (e) {
      toast.error(explainError(e));
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await gitApi.deleteBranch(owner, name, deleteTarget);
      toast.success(`分支 ${deleteTarget} 已删除`);
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      toast.error(explainError(e));
    } finally {
      setDeleting(false);
    }
  };

  const doRename = async () => {
    const to = renameTo.trim();
    if (!to || !renaming) return;
    try {
      await gitApi.renameBranch(owner, name, renaming, to);
      toast.success('分支已重命名');
      setRenaming(null);
      setRenameTo('');
      refresh();
    } catch (e) {
      toast.error(explainError(e));
    }
  };

  return (
    <Card size="small">
      <div className={styles.panelBody}>
        <div className={styles.branchToolbar}>
          <Text className={styles.commitMeta}>{branches.length} 个分支</Text>
          {canPush && (
            <Button
              appearance="primary"
              icon={<AddRegular />}
              onClick={() => {
                setFromBranch(defaultBranch);
                setNewName('');
                setCreateOpen(true);
              }}
            >
              新建分支
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className={styles.loadingCenter}>
            <Spinner />
          </div>
        ) : branches.length === 0 ? (
          <div className={styles.emptyCenter}>
            <BranchForkRegular />
            <span>暂无分支</span>
          </div>
        ) : (
          <div className={styles.branchList}>
            {branches.map((b) => (
              <div key={b} className={styles.branchRow}>
                <BranchForkRegular />
                <span className={styles.branchName}>{b}</span>
                {b === defaultBranch && (
                  <Badge appearance="tint" color="brand">
                    default
                  </Badge>
                )}
                {canPush && b !== defaultBranch && (
                  <div className={styles.branchActions}>
                    <Button
                      size="small"
                      appearance="subtle"
                      icon={<EditRegular />}
                      onClick={() => {
                        setRenaming(b);
                        setRenameTo(b);
                      }}
                    />
                    <Button
                      size="small"
                      appearance="subtle"
                      className={styles.dangerButton}
                      icon={<DeleteRegular />}
                      onClick={() => setDeleteTarget(b)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 新建分支 Dialog */}
      <Dialog
        modalType="alert"
        open={createOpen}
        onOpenChange={(_, d) => {
          if (!d.open) setCreateOpen(false);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>新建分支</DialogTitle>
            <DialogContent>
              <div className={styles.settingsField}>
                <Label htmlFor={fromId} className={styles.fieldLabel}>
                  基于分支 <HelpPopover content="新分支将从哪个已有分支创建,默认选择仓库的默认分支。" />
                </Label>
                <Combobox
                  id={fromId}
                  value={fromBranch}
                  selectedOptions={[fromBranch]}
                  onOptionSelect={(_, d) => {
                    const v = d.selectedOptions[0];
                    if (v) setFromBranch(v);
                  }}
                >
                  {branches.map((b) => (
                    <Option key={b} value={b}>
                      {b}
                    </Option>
                  ))}
                </Combobox>
              </div>
              <div className={styles.settingsField}>
                <Label htmlFor={newNameId} className={styles.fieldLabel}>
                  新分支名 <HelpPopover content="新分支的名称,例如 feature/xxx。命名需在该仓库内唯一。" />
                </Label>
                <Input
                  id={newNameId}
                  value={newName}
                  onChange={(_, d) => setNewName(d.value)}
                  placeholder="如 feature/xxx"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') doCreate();
                  }}
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setCreateOpen(false)}>
                取消
              </Button>
              <Button appearance="primary" onClick={doCreate}>
                创建
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 重命名分支 Dialog */}
      <Dialog
        modalType="alert"
        open={renaming !== null}
        onOpenChange={(_, d) => {
          if (!d.open) {
            setRenaming(null);
            setRenameTo('');
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>重命名分支 {renaming ?? ''}</DialogTitle>
            <DialogContent>
              <div className={styles.settingsField}>
                <Label htmlFor={renameId} className={styles.fieldLabel}>
                  新分支名 <HelpPopover content="分支的新名称。重命名不会修改其中的提交历史,只是改了引用名。" />
                </Label>
                <Input
                  id={renameId}
                  value={renameTo}
                  onChange={(_, d) => setRenameTo(d.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') doRename();
                  }}
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => {
                  setRenaming(null);
                  setRenameTo('');
                }}
              >
                取消
              </Button>
              <Button appearance="primary" onClick={doRename}>
                重命名
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 删除分支确认 */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title={`确认删除分支 ${deleteTarget ?? ''}?`}
        content="此操作不可恢复"
        confirmText="删除"
        danger
        loading={deleting}
        onConfirm={doDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </Card>
  );
}

// ============================================================
// CollaboratorsTab — 协作者管理
// ============================================================
const COLLAB_ROLE_META: Record<
  Collaborator['role'],
  {
    label: string;
    color: 'informative' | 'brand' | 'success' | 'warning' | 'danger';
    desc: string;
  }
> = {
  read: { label: '只读', color: 'informative', desc: '克隆与浏览' },
  triage: { label: '处理问题', color: 'brand', desc: '管理 issues / labels' },
  write: { label: '开发', color: 'success', desc: '可 push 代码' },
  maintain: { label: '维护', color: 'warning', desc: '管理设置与协作者' },
  admin: { label: '管理员', color: 'danger', desc: '可删除 / 转移仓库' },
};
const COLLAB_ROLE_OPTIONS = (
  Object.keys(COLLAB_ROLE_META) as Collaborator['role'][]
).map((r) => ({
  value: r,
  label: `${COLLAB_ROLE_META[r].label}（${COLLAB_ROLE_META[r].desc}）`,
}));

function CollaboratorsTab(props: { owner: string; name: string }) {
  const { owner, name } = props;
  const styles = useStyles();
  const qc = useQueryClient();
  const [newUser, setNewUser] = useState('');
  const [newRole, setNewRole] = useState<Collaborator['role']>('write');
  const [adding, setAdding] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  const { data: collabs = [], isLoading, error } = useQuery({
    queryKey: ['repo-collaborators', owner, name],
    queryFn: () => reposApi.listCollaborators(owner, name),
  });

  const refresh = () =>
    qc.invalidateQueries({ queryKey: ['repo-collaborators', owner, name] });

  const doAdd = async () => {
    const username = newUser.trim();
    if (!username) {
      toast.warning('请输入用户名');
      return;
    }
    setAdding(true);
    try {
      await reposApi.addCollaborator(owner, name, username, newRole);
      toast.success(
        `已添加协作者 ${username}（${COLLAB_ROLE_META[newRole].label}）`,
      );
      setNewUser('');
      setNewRole('write');
      refresh();
    } catch (e) {
      toast.error(explainError(e));
    } finally {
      setAdding(false);
    }
  };

  const doRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await reposApi.removeCollaborator(owner, name, removeTarget);
      toast.success(`已移除协作者 ${removeTarget}`);
      setRemoveTarget(null);
      refresh();
    } catch (e) {
      toast.error(explainError(e));
    } finally {
      setRemoving(false);
    }
  };

  if (error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{explainError(error)}</MessageBarBody>
      </MessageBar>
    );
  }

  return (
    <Card size="small">
      <CardHeader
        header={
          <span className={styles.cardHeaderRow}>
            <PeopleTeamRegular />
            <span>协作者</span>
          </span>
        }
      />
      <div className={styles.panelBody}>
        <div className={styles.collabSection}>
          <MessageBar intent="info">
            <MessageBarBody>
              所有者（Owner）默认拥有管理员权限，无需在此添加。{owner} 是该仓库的所有者，具备完整权限。
            </MessageBarBody>
          </MessageBar>

          <div>
            <Text className={styles.fieldLabel}>
              添加协作者 <HelpPopover content="输入一个本地用户名并选择角色,点击「添加」将其设为协作者。所有者默认拥有完整权限,无需在此添加。" />
            </Text>
            <div
              className={styles.collabAddRow}
              style={{ alignItems: 'flex-end' }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  flex: 1,
                  minWidth: 160,
                }}
              >
                <Label size="small">
                  用户名 <HelpPopover content="要添加为协作者的本地用户名。" />
                </Label>
                <Input
                  placeholder="用户名"
                  value={newUser}
                  onChange={(_, d) => setNewUser(d.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') doAdd();
                  }}
                  aria-label="用户名"
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  width: 220,
                }}
              >
                <Label size="small">
                  角色 <HelpPopover content="协作者权限级别:只读(克隆与浏览)、处理问题、开发(可 push 代码)、维护(管理设置与协作者)、管理员(可删除 / 转移仓库)。" />
                </Label>
                <Combobox
                  value={newRole}
                  selectedOptions={[newRole]}
                  onOptionSelect={(_, d) => {
                    const v = d.selectedOptions[0] as Collaborator['role'] | undefined;
                    if (v) setNewRole(v);
                  }}
                  aria-label="角色"
                >
                  {COLLAB_ROLE_OPTIONS.map((o) => (
                    <Option key={o.value} value={o.value}>
                      {o.label}
                    </Option>
                  ))}
                </Combobox>
              </div>
              <Button
                appearance="primary"
                icon={<AddRegular />}
                onClick={doAdd}
                disabled={adding}
              >
                {adding ? '添加中…' : '添加'}
              </Button>
            </div>
            <Text block className={styles.fieldHint}>
              角色说明：开发可 push 代码；维护可管理仓库设置与协作者；管理员可删除 / 转移仓库。
            </Text>
          </div>

          {isLoading ? (
            <div className={styles.loadingCenter}>
              <Spinner />
            </div>
          ) : collabs.length === 0 ? (
            <div className={styles.emptyCenter}>
              <PeopleTeamRegular />
              <span>暂无协作者</span>
            </div>
          ) : (
            <div>
              {collabs.map((c) => (
                <div key={c.username} className={styles.collabRow}>
                  <Avatar
                    size={36}
                    name={c.username}
                    color="brand"
                    idForColor={c.username}
                  />
                  <div className={styles.collabMain}>
                    <span className={styles.collabName}>
                      <span style={{ fontWeight: 600 }}>{c.username}</span>
                      <Badge
                        appearance="tint"
                        color={COLLAB_ROLE_META[c.role].color}
                      >
                        {COLLAB_ROLE_META[c.role].label}
                      </Badge>
                    </span>
                    <span className={styles.collabDesc}>
                      授权于 {new Date(c.granted_at).toLocaleString()}
                    </span>
                  </div>
                  <Button
                    size="small"
                    appearance="subtle"
                    className={styles.dangerButton}
                    icon={<DeleteRegular />}
                    onClick={() => setRemoveTarget(c.username)}
                  >
                    移除
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 移除协作者确认 */}
      <ConfirmDialog
        open={removeTarget !== null}
        title={`移除协作者 ${removeTarget ?? ''}？`}
        confirmText="移除"
        danger
        loading={removing}
        onConfirm={doRemove}
        onClose={() => setRemoveTarget(null)}
      />
    </Card>
  );
}

// ============================================================
// SettingsTab — 仓库设置
// ============================================================
function SettingsTab(props: {
  owner: string;
  name: string;
  canAdmin: boolean;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const { owner, name, canAdmin, onSaved, onDeleted } = props;
  const styles = useStyles();
  const { data: repo } = useQuery({
    queryKey: ['repo', owner, name],
    queryFn: () => reposApi.getRepo(owner, name),
  });
  const [desc, setDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (repo) setDesc(repo.description ?? '');
  }, [repo]);

  async function onSave() {
    setSaving(true);
    try {
      await reposApi.update(owner, name, { description: desc || null });
      toast.success('已保存');
      onSaved();
    } catch (e) {
      toast.error(explainError(e));
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    setDeleting(true);
    try {
      await reposApi.delete(owner, name);
      toast.success('仓库已删除');
      onDeleted();
    } catch (e) {
      toast.error(explainError(e));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Card size="small">
      <CardHeader
        header={
          <span className={styles.cardHeaderRow}>
            <SettingsRegular />
            <span>仓库设置</span>
          </span>
        }
      />
      <div className={styles.panelBody}>
        <div className={styles.settingsSection}>
          <div className={styles.settingsField}>
            <Text className={styles.fieldLabel}>
              描述 <HelpPopover content="仓库的一句话描述,最多 500 字,会展示在仓库主页与列表页。可留空。" />
            </Text>
            <Textarea
              aria-label="描述"
              value={desc}
              onChange={(_, d) => setDesc(d.value)}
              placeholder="一句话描述这个仓库..."
              rows={3}
              maxLength={500}
              resize="vertical"
            />
            <Text className={styles.charCount}>{desc.length}/500</Text>
          </div>
          <div>
            <Button appearance="primary" disabled={saving} onClick={onSave}>
              {saving ? '保存中…' : '保存'}
            </Button>
          </div>
          <Text block className={styles.fieldHint}>
            改 visibility / default_branch — 后续版本支持（协作者管理见「协作者」Tab）
          </Text>

          {/* ═══ 危险操作区(仅 Admin 可见) ═══ */}
          {canAdmin && (
            <>
              <Divider
                style={{ borderColor: tokens.colorStatusDangerBorder1 }}
              />
              <div className={styles.dangerBlock}>
                <Text className={styles.dangerTitle}>危险操作</Text>
                <MessageBar intent="warning">
                  <MessageBarBody>
                    删除仓库不可恢复：删除后所有代码、提交历史与协作者配置将永久丢失，且无法恢复。
                  </MessageBarBody>
                </MessageBar>
                <Button
                  appearance="primary"
                  className={styles.dangerButton}
                  disabled={deleting}
                  onClick={() => setDeleteOpen(true)}
                >
                  删除仓库
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 删除仓库确认 */}
      <ConfirmDialog
        open={deleteOpen}
        title="确认删除该仓库？"
        content={`仓库 ${owner}/${name} 将被永久删除`}
        confirmText="删除"
        danger
        loading={deleting}
        onConfirm={onDelete}
        onClose={() => setDeleteOpen(false)}
      />
    </Card>
  );
}
